// FPS telemetry (IMPLEMENTATION §2.9 `fps (p50/p5 per minute)`): the frame intervals of each `periodMs` of
// wall time (visible frames only) reduced to the median and the 5th-percentile frame rate, plus a count of
// HITCHES (intervals over `hitchMs`, render.frameClampMs 250: GC, a long save or telemetry flush — rare, so
// a 5th percentile over thousands of frames would hide them). PURE: the app feeds rAF timestamps
// (`frame(now)`); `flush()` (on hide) ends the period and forgets the last frame, so the hidden time is never
// an interval; an interval over `gapMs` (ui.json debug.telemetryFpsGapMs: a debugger pause, a suspend without
// visibilitychange) is a gap, not a frame. Percentiles are nearest-rank over the per-frame rates.

export interface FpsSample {
  /** Median frame rate (fps) over the period. */
  p50: number;
  /** 5th-percentile frame rate: 95% of the frames were at least this fast. */
  p5: number;
  /** Frames measured. */
  frames: number;
  /** Frames longer than hitchMs (they are in the percentiles too). */
  hitches: number;
}

export interface FpsSamplerConfig {
  /** Reporting period in ms (ui.json debug.telemetryPeriodSec × 1000). */
  periodMs: number;
  /** A frame longer than this is a hitch (ui.json render.frameClampMs). */
  hitchMs: number;
  /** Longer intervals are pauses, not frames (ui.json debug.telemetryFpsGapMs). */
  gapMs: number;
}

export interface FpsSampler {
  /** Feeds one frame timestamp; returns the finished period's sample when `periodMs` elapsed. */
  frame(now: number): FpsSample | null;
  /** The partial period so far (null without frames); starts a new period after the next frame (on hide). */
  flush(): FpsSample | null;
}

/** Nearest-rank percentile (p in 0…100) of an ascending array; NaN when empty. */
export function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return Number.NaN;
  const rank = Math.ceil((p / 100) * sorted.length);
  return sorted[Math.min(sorted.length, Math.max(1, rank)) - 1] as number;
}

export function summarizeFrames(
  intervalsMs: readonly number[],
  hitchMs = Number.POSITIVE_INFINITY,
): FpsSample | null {
  if (intervalsMs.length === 0) return null;
  const fps = intervalsMs.map((dt) => 1000 / dt).sort((a, b) => a - b);
  const r1 = (n: number): number => Math.round(n * 10) / 10;
  let hitches = 0;
  for (const dt of intervalsMs) if (dt > hitchMs) hitches += 1;
  return { p50: r1(percentile(fps, 50)), p5: r1(percentile(fps, 5)), frames: fps.length, hitches };
}

export function createFpsSampler(cfg: FpsSamplerConfig): FpsSampler {
  let last: number | null = null;
  let start: number | null = null;
  let dts: number[] = [];
  const take = (): FpsSample | null => {
    const s = summarizeFrames(dts, cfg.hitchMs);
    dts = [];
    start = last;
    return s;
  };
  return {
    frame(now) {
      if (last !== null) {
        const dt = now - last;
        if (dt > 0 && dt <= cfg.gapMs) dts.push(dt);
      }
      last = now;
      if (start === null) start = now;
      return now - start >= cfg.periodMs ? take() : null;
    },
    flush() {
      const s = take();
      last = null; // hidden: rAF stops; the first frame back starts a new interval chain
      start = null;
      return s;
    },
  };
}
